"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { CookiesProvider } from "react-cookie";
import {
  AuthProvider,
  LayoutProvider,
  NotificationProvider,
  PartnerPortalAuthProvider,
  SearchCriteriaProvider,
  SiteGlobalsProvider,
  SiteTripProvider,
} from "@/states";
import SiteErrorBoundary from "@/components/SiteErrorBoundary";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import { DefaultLayout } from "@/layouts";
import { useDisableBrowserAutocomplete } from "@/hooks/useDisableBrowserAutocomplete";
import { warmMediaConnections } from "@/utils/imageUrl";

const AppShell = ({ children }) => {
  const pathname = usePathname();
  const prevPathnameRef = useRef(pathname);
  useDisableBrowserAutocomplete();

  useEffect(() => {
    warmMediaConnections();
    if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    const stickyTimer = window.setTimeout(() => {
      document.documentElement.classList.add("header-sticky-ready");
    }, 100);
    return () => window.clearTimeout(stickyTimer);
  }, []);

  useEffect(() => {
    if (prevPathnameRef.current === pathname) return;
    prevPathnameRef.current = pathname;
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <CookiesProvider>
      <SiteGlobalsProvider>
        <NotificationProvider>
          <LayoutProvider>
            <AuthProvider>
              <PartnerPortalAuthProvider>
                <SearchCriteriaProvider>
                  <SiteTripProvider>
                    <SiteErrorBoundary>
                      <DefaultLayout>{children}</DefaultLayout>
                    </SiteErrorBoundary>
                    <CookieConsentBanner />
                  </SiteTripProvider>
                </SearchCriteriaProvider>
              </PartnerPortalAuthProvider>
            </AuthProvider>
          </LayoutProvider>
        </NotificationProvider>
      </SiteGlobalsProvider>
    </CookiesProvider>
  );
};

export default AppShell;
